import Button from "components/Buttons/Button/Button";
import FiltersModal from "components/layout/MobileDialogs/FiltersModal/FiltersModal";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import boxesImg from "../../../../assets/images/icons/boxes.png";
import listImg from "../../../../assets/images/icons/list2.png";
import UserPreferences from "../../../../helpers/UserPreferences";
import scss from "./Controls.module.scss";
import { useTranslation } from "react-i18next";
import { Icon, IconButton, Menu, MenuItem } from "@mui/material";
import useAPI from "hooks/useAPI";
import useDefaultProductSort, { USER_SORT_STORAGE_KEY } from "hooks/useDefaultProductSort";

/**
 * Maps a `field___direction` sort option id (as managed in CMS Params, group
 * `product_sort_options_b2b`) to the translation key suffix used for its label.
 * An id without an entry here falls back to the label sent by the backend.
 */
const SORT_OPTION_TRANSLATION_KEYS = {
    new___desc: "new",
    price___asc: "by_price_ascending",
    price___desc: "by_price_descending",
    name___asc: "by_name_ascending",
    name___desc: "by_name_descending",
    inventory___asc: "in_lager_ascending",
    inventory___desc: "in_lager_descending",
};

/** Shown while the sort options are loading from the backend, or if that request fails. */
const FALLBACK_SORT_OPTIONS = [
    { id: "new___desc", name: "Novo" },
    { id: "price___asc", name: "Po ceni, rastuće" },
    { id: "price___desc", name: "Po ceni, opadajuće" },
    { id: "name___asc", name: "Po nazivu, rastuće" },
    { id: "name___desc", name: "Po nazivu, opadajuće" },
    { id: "inventory___asc", name: "Na stanju, rastuće" },
    { id: "inventory___desc", name: "Na stanju, opadajuće" },
];

/**
 * Show controls for the presentation of the products.
 *
 * @param {?string} title The optional title to show, or null to hide the controls.
 * @param {function({ limit: int, view: string, order: string })} onParamChange Invoked when any of the parameters change.
 *
 * @return {JSX.Element}
 * @constructor
 */

const Controls = ({
    showFilters = true,
    title,
    onParamChange,
    filters,
    setActiveFilters,
    numberOfProducts,
    setSort,
    categoryid,
    sort,
    setLimit,
    limit,
    setCurrentPage,
    changeFilters,
    isFetched,
    lastSelectedFilterKey,
    setChangeFilters,
    setLastSelectedFilterKey,
    newQuery,
    setNewQuery,
    selectedFilters,
    availableFilters,
    setSelectedFilters,
    translateKey,
}) => {
    const { t } = useTranslation();
    const api = useAPI();
    const { secondary: defaultSecondarySort } = useDefaultProductSort();

    const [openFilters, setOpenFilters] = useState(false);

    // Send updated values to the parent
    const updateParent = () => onParamChange && onParamChange({ ready: true, ...UserPreferences.get("presentation") });
    useEffect(updateParent, []);
    // Local state: The number of products to show
    // const [limit, setLimit] = useState(UserPreferences.get("presentation.limit"));
    // useEffect(() => UserPreferences.set("presentation.limit", limit) && updateParent(), [limit]);

    // Local state: The order of the products
    const [order, setOrder] = useState(UserPreferences.get("presentation.order"));
    useEffect(() => UserPreferences.set("presentation.order", order) && updateParent(), [order]);

    const [view, setView] = useState(UserPreferences.get("presentation.view"));
    useEffect(() => UserPreferences.set("presentation.view", view) && updateParent(), [view]);

    const [optionsAnchor, setOptionsAnchor] = useState(null);
    const handlePrint = () => {
        setOptionsAnchor(null);
        window.print();
    };

    // The available "order by" options, sourced from CMS Params (group `product_sort_options_b2b`,
    // the same list managed under "Podrazumevano sortiranje proizvoda u listi").
    const { data: sortOptionsData } = useQuery(["productSortOptions"], () => api.get("/options/global/product-sort-options"), {
        refetchOnWindowFocus: false,
        staleTime: 60 * 60 * 1000,
    });

    const optionsArray = (sortOptionsData ?? FALLBACK_SORT_OPTIONS).map((option) => {
        const [field, direction] = option.id.split("___");
        const translationKeySuffix = SORT_OPTION_TRANSLATION_KEYS[option.id];
        return {
            value: { field, direction },
            label: translationKeySuffix ? t(`${translateKey}-controls-${translationKeySuffix}`) : option.name,
        };
    });

    const limitArray = [
        { value: 4, label: 4 },
        { value: 8, label: 8 },
        { value: 12, label: 12 },
        { value: 18, label: 18 },
        { value: 24, label: 24 },
    ];
    return (
        <>
            <div className={scss.wrapper + " col-12"}>
                <span className={scss.title}>{title}</span>
                <span className={`${scss.number} desktop-hidden`}>{numberOfProducts && t(`${translateKey}-controls-total_products`) + ": " + numberOfProducts}</span>
                <div className={scss.presentationControl}>
                    {showFilters && (
                        <div className={scss.filters}>
                            <Button className={scss.filtersButton} onClick={() => setOpenFilters(!openFilters)} label={t(`${translateKey}-controls-filters`)} />
                        </div>
                    )}
                    {/* Order */}
                    <div
                        className={scss.control}
                        style={{
                            gridColumn: showFilters ? "span 1" : "span 2",
                        }}
                    >
                        <select
                            style={{
                                height: showFilters ? "100%" : "200%",
                            }}
                            className={scss.select}
                            onChange={(e) => {
                                const [field, direction] = e.target.value.split(",");
                                // Remember the user's own choice so it's honored on future page
                                // loads, ahead of the CMS default (but behind an explicit URL sort).
                                localStorage.setItem(USER_SORT_STORAGE_KEY, `${field}___${direction}`);
                                setSort([...defaultSecondarySort, { field, direction }]);
                                setCurrentPage(1);
                            }}
                            value={`${sort[sort.length - 1]?.field},${sort[sort.length - 1]?.direction}`}
                        >
                            {optionsArray.map((option, index) => (
                                <option key={index} value={`${option.value.field},${option.value.direction}`}>
                                    {option.label}
                                </option>
                            ))}
                        </select>

                        {/* <img src={icons.sort} alt={icons.sort} height="20rem" /> */}
                    </div>

                    {/* Limit */}
                    <div className={`${scss.control} mobile-hidden`}>
                        <select
                            defaultValue={limit}
                            value={limit}
                            className={scss.select}
                            onChange={(e) => {
                                setLimit(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            {limitArray.map((option, index) => (
                                <option key={index} value={option.value}>
                                    {t(`${translateKey}-controls-visibly`)}: {option.label}
                                </option>
                            ))}
                        </select>

                        {/* <img src={icons.view} alt={icons.view} height="20rem" /> */}
                    </div>

                    {/* View */}
                    <div className={scss.control}>
                        <div className={view === ProductsView.Grid ? scss.activeIcon : scss.inactiveIcon}>
                            <button className={scss.iconButton} onClick={() => setView(ProductsView.Grid)}>
                                <img src={boxesImg} alt={boxesImg} />
                            </button>
                        </div>

                        <div className={view === ProductsView.List ? scss.activeIcon : scss.inactiveIcon}>
                            <button className={scss.iconButton} onClick={() => setView(ProductsView.List)}>
                                <img src={listImg} alt={listImg} />
                            </button>
                        </div>
                    </div>

                    {/* Options */}
                    <div className={`${scss.control} mobile-hidden`}>
                        <IconButton className={scss.optionsButton} size="small" onClick={(e) => setOptionsAnchor(e.currentTarget)}>
                            <Icon>more_vert</Icon>
                        </IconButton>
                        <Menu anchorEl={optionsAnchor} open={Boolean(optionsAnchor)} onClose={() => setOptionsAnchor(null)}>
                            <MenuItem onClick={handlePrint}>{t(`${translateKey}-controls-print`)}</MenuItem>
                        </Menu>
                    </div>
                </div>
            </div>
            <FiltersModal
                openModal={openFilters}
                setOpenModal={setOpenFilters}
                filters={filters}
                setActiveFilters={setActiveFilters}
                changeFilters={changeFilters}
                isFetched={isFetched}
                lastSelectedFilterKey={lastSelectedFilterKey}
                setChangeFilters={setChangeFilters}
                setLastSelectedFilterKey={setLastSelectedFilterKey}
                newQuery={newQuery}
                selectedFilters={selectedFilters}
                availableFilters={availableFilters}
                setSelectedFilters={setSelectedFilters}
                setCurrentPage={setCurrentPage}
                categoryid={categoryid}
                setNewQuery={setNewQuery}
            />
        </>
    );
};

/** The list of possible views for the list of products. */
export const ProductsView = {
    Grid: "grid",
    List: "list",
};

/** The list of possible number of products per page. */
export const ProductsLimit = {
    _4: 4,
    _8: 8,
    _12: 12,
    _18: 18,
    _24: 24,
};

/** The list of possible order variations. */
export const ProductsOrder = {
    ASC: "asc",
    DESC: "desc",
};

/**
 * The default controls for the presentation of products.
 *
 * @typedef PresentationControls
 *      @property {int} limit
 *      @property {string} view
 *      @property {string} order
 */
export const DefaultProductsControls = {
    page: 1,
    view: ProductsView.Grid,
    limit: ProductsLimit._8,
    order: ProductsOrder.DESC,
};

export default Controls;
